import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { AddressAutocompleteInput } from "components/AddressAutocompleteInput";
import { ToastError } from "utils/toasts";
import { createMemberByMrn } from "services/memberMrn";

const genderOptions = ["Male", "Female", "Other"];

const defaultValues = {
  firstName: "",
  lastName: "",
  gender: genderOptions[0],
  medicalRecordNumber: "",
  mobileNumber: "",
  address: "",
  addressLat: null,
  addressLng: null,
  addressStreet: "",
  addressCity: "",
  addressState: "",
  addressZip: "",
  addressCountry: "",
};

function fieldClass(hasError) {
  return `w-full rounded-2xl bg-gray-100 px-4 py-3.5 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:bg-white transition disabled:opacity-50 ${
    hasError ? "ring-2 ring-red-400" : "focus:ring-[#293562]"
  }`;
}

const labelClass = "block text-xs font-semibold text-gray-500 mb-1.5";

function FormField({ label, required, error, children }) {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && " *"}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function GenderToggle({ value, onChange }) {
  return (
    <div className="flex rounded-2xl bg-gray-100 p-1">
      {genderOptions.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={`flex-1 rounded-xl py-2.5 text-sm font-medium transition ${
            value === option
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500"
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

export default function CreateMemberPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fundingsourceId, memberId } = useParams();
  const fundingSource = location.state?.fundingSource ?? null;

  const {
    register,
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: { ...defaultValues, medicalRecordNumber: memberId || "" },
  });

  async function onSubmit(values) {
    try {
      const payload = {
        mrn: values.medicalRecordNumber.trim(),
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        gender: values.gender,
        contactNumber: values.mobileNumber.trim(),
        homeAddress: {
          address: values.address.trim(),
          street: values.addressStreet || values.address.trim(),
          city: values.addressCity,
          state: values.addressState,
          zipCode: values.addressZip,
          country: values.addressCountry || "USA",
          latitude: values.addressLat ?? null,
          longitude: values.addressLng ?? null,
        },
      };
      await createMemberByMrn(payload, fundingsourceId);

      // Go back to the search screen so the user can re-search by MRN
      // with a clean session — avoids stale data / "not found" flickers.
      // Pre-fill the search field with the newly created MRN.
      navigate(`/fundingsource/${fundingsourceId}`, {
        replace: true,
        state: { prefillMrn: payload.mrn },
      });
    } catch (err) {
      ToastError(err.message || "Unable to create member. Please try again.");
    }
  }

  return (
    <section className="min-h-dvh bg-gradient-to-br from-[#eef1f8] via-[#f5f5f7] to-[#e9ecf3] flex items-center justify-center p-4">
      <div className="w-full max-w-lg h-[calc(100dvh-2rem)] bg-white/90 backdrop-blur-xl rounded-3xl shadow-[0_10px_40px_rgb(0,0,0,0.08)] ring-1 ring-black/5 flex flex-col overflow-hidden">

        {/* Fixed top */}
        <div className="shrink-0 px-6 pt-4 pb-3 space-y-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">Create Member</h2>
        </div>

        {/* Form: scrollable fields + fixed bottom button */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex-1 flex flex-col min-h-0 overflow-hidden"
          noValidate
        >
          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="First Name" required error={errors.firstName?.message}>
                <input
                  {...register("firstName", { required: "First name is required" })}
                  placeholder="John"
                  className={fieldClass(!!errors.firstName)}
                />
              </FormField>
              <FormField label="Last Name" required error={errors.lastName?.message}>
                <input
                  {...register("lastName", { required: "Last name is required" })}
                  placeholder="Doe"
                  className={fieldClass(!!errors.lastName)}
                />
              </FormField>
            </div>

            <FormField label="Gender" required error={errors.gender?.message}>
              <Controller
                name="gender"
                control={control}
                rules={{ required: "Gender is required" }}
                render={({ field }) => (
                  <GenderToggle value={field.value} onChange={field.onChange} />
                )}
              />
            </FormField>

            <FormField
              label="Medical Record Number"
              required
              error={errors.medicalRecordNumber?.message}
            >
              <input
                {...register("medicalRecordNumber", {
                  required: "Medical record number is required",
                })}
                placeholder="MRN-000000"
                className={fieldClass(!!errors.medicalRecordNumber)}
              />
            </FormField>

            <FormField label="Mobile Number" required error={errors.mobileNumber?.message}>
              <input
                type="tel"
                inputMode="numeric"
                onKeyDown={(e) => {
                  if (
                    e.key.length === 1 &&
                    !/[0-9]/.test(e.key) &&
                    !e.ctrlKey &&
                    !e.metaKey
                  ) {
                    e.preventDefault();
                  }
                }}
                {...register("mobileNumber", {
                  required: "Mobile number is required",
                  validate: (value) => {
                    const digits = value.replace(/\D/g, "");
                    if (digits.length < 10 || digits.length > 11) {
                      return "Mobile number must be 10-11 digits";
                    }
                    return true;
                  },
                })}
                placeholder="(555) 000-0000"
                className={fieldClass(!!errors.mobileNumber)}
              />
            </FormField>

            <FormField label="Address" required error={errors.address?.message}>
              <Controller
                name="address"
                control={control}
                rules={{
                  required: "Address is required",
                  validate: () =>
                    (watch("addressLat") != null && watch("addressLng") != null) ||
                    "Please select an address from the dropdown",
                }}
                render={({ field }) => (
                  <AddressAutocompleteInput
                    value={field.value}
                    lat={watch("addressLat")}
                    lng={watch("addressLng")}
                    components={{
                      street: watch("addressStreet"),
                      city: watch("addressCity"),
                      state: watch("addressState"),
                      zipCode: watch("addressZip"),
                      country: watch("addressCountry"),
                    }}
                    onChange={({ address, lat, lng, components }) => {
                      setValue("addressLat", lat);
                      setValue("addressLng", lng);
                      setValue("addressStreet", components?.street || "");
                      setValue("addressCity", components?.city || "");
                      setValue("addressState", components?.state || "");
                      setValue("addressZip", components?.zipCode || "");
                      setValue("addressCountry", components?.country || "");
                      field.onChange(address);
                    }}
                    placeholder="123 Main St, City, State, ZIP"
                    className={fieldClass(!!errors.address)}
                  />
                )}
              />
            </FormField>
          </div>

          {/* Fixed bottom button */}
          <div className="shrink-0 px-6 pb-6 pt-3 border-t border-gray-100">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-2xl bg-[#293562] py-4 text-lg font-semibold text-white shadow-sm hover:brightness-95 transition disabled:opacity-60"
            >
              {isSubmitting ? "Creating..." : "Create Member"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
